import InstancePlugin from '../../Core/mixin/InstancePlugin.js';
import GridFeatureManager from './GridFeatureManager.js';
import Popup from '../../Core/widget/Popup.js';
import ObjectHelper from '../../Core/helper/ObjectHelper.js';
import '../widget/GridChartDesigner.js';

/**
 * @module Grid/feature/Charts
 */

/**
 * Adds interactive charting to a Grid. {@link Chart.widget.Chart Charts} can be created from a selection of Grid data
 * and updated in realtime as data changes. Supports many common chart types with extensive styling and customization
 * options.
 *
 * {@inlineexample Grid/feature/Charts.js}
 *
 * A context menu item `New Chart` is added that opens a popup containing a {@link Chart.widget.ChartDesigner} using
 * the data in the grid's current selection.
 *
 * For an explanation of the various options available in the editor popup, see {@link Chart.widget.Chart}.
 *
 * This feature is **disabled** by default.
 *
 * ```javascript
 * const grid = new Grid({
 *     features : {
 *         charts : true
 *     }
 * });
 * ```
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Grid/charts
 * @classtype charts
 * @feature
 */
export default class Charts extends InstancePlugin {

    // <remove-on-release>
    // Localization strings below are required due to charts localizations being included in module, but not the code
    // </remove-on-release>
    // <debug>
    // region Localization
    // Do not remove. Assertion strings for Localization sanity check
    'L{Charts.newChart}';
    // endregion
    // </debug>

    static $name = 'Charts';
    static type  = 'charts';

    static get pluginConfig() {
        return {
            chain : [
                'populateCellMenu'
            ]
        };
    }

    static configurable = {
        /**
         * (Optional) Configuration options to provide to the {@link Core.widget.Popup}.
         * @config {PopupConfig}
         */
        popup : {
            $config : ['lazy', 'nullify'],
            value   : {
                cls          : 'b-chartdesigner-popup',
                scrollAction : 'realign',
                title        : 'L{Charts.chartDesigner}',
                autoShow     : false,
                autoClose    : false,
                closable     : true,
                draggable    : true,
                maximizable  : true,
                resizable    : true,
                width        : '60vw',
                height       : 'calc(min(90vh, 33vw))',
                centered     : true,
                padding      : 0,
                ui           : 'plain',
                layout       : {
                    type  : 'vbox',
                    align : 'stretch'
                }
            }
        },

        /**
         * (Optional) Configuration options to provide to the {@link Chart.widget.ChartDesigner}
         * @config {Object}
         */
        chartDesigner : {
            $config : ['lazy', 'nullify'],
            value   : {
                flex              : 1,
                align             : 'stretch',
                width             : '100%',
                padding           : 0,
                margin            : 0,
                ui                : 'plain',
                showChartControls : false
            }
        },

        /**
         * Whether to display in minimal mode, where popup title is hidden, chart preview occupies full area,
         * and settings panel is minimized. Setting this to `true` also sets `minimal : true` on the child
         * {@link Chart.widget.ChartDesigner}.
         * @prp {Boolean}
         * @default
         */
        minimal : false
    };

    construct(client, config) {
        super.construct(client, config);
        this.popups = new Set();
    }

    doDestroy() {
        for (const popup of this.popups) {
            popup.destroy();
        }
        this.popups.clear();
        this.detachListeners('gridSelection');
        super.doDestroy();
    }

    populateCellMenu({ record, items }) {
        items.newChart = {
            text        : 'L{newChart}',
            localeClass : this,
            icon        : 'b-icon b-icon-chart',
            weight      : 145,
            separator   : true,
            onItem      : this.openPopup.bind(this)
        };
    }

    /**
     * Open the chart designer popup with the current Grid selection as data source.
     */
    async openPopup() {
        const
            me = this,
            { chartDesigner, client : grid, minimal } = me,
            popup = Popup.new(
                {
                    forElement  : grid.element,
                    owner       : grid,
                    rootElement : me.owner.rootElement,
                    tools       : {
                        download : {
                            cls       : 'b-icon b-icon-download',
                            tooltip   : 'L{Charts.download}',
                            ariaLabel : 'L{Charts.download}',
                            handler   : (event, panel) => {
                                panel.widgetMap.preview.download();
                            }
                        },
                        settings : {
                            cls       : 'b-icon b-icon-settings',
                            tooltip   : 'L{Charts.settings}',
                            ariaLabel : 'L{Charts.settings}',
                            handler   : (event, panel) => {
                                const { chartDesigner } = panel.widgetMap;
                                chartDesigner.minimal = !chartDesigner.minimal;
                            }
                        }
                    },
                    internalListeners : {
                        destroy : ({ source }) => {
                            me.popups?.remove(source);
                        }
                    }
                },
                {
                    title  : minimal ? null : me.title,
                    header : !minimal
                },
                me.popup,
                {
                    items : {
                        chartDesigner : ObjectHelper.assign({
                            type        : 'gridchartdesigner',
                            grid,
                            rootElement : me.owner.rootElement,
                            minimal
                        }, chartDesigner)
                    }
                }
            );
        popup.show();
    }

}

GridFeatureManager.registerFeature(Charts, false, 'Grid');
